import { NavLink } from 'react-router-dom';
import {
    AppleIcon,
    BookIcon,
    BracketsIcon,
    CalendarIcon2,
    CardsIcon, CheckIcon,
    CloseIcon, DiagramIcon,
    EditIcon, HouseIcon,
    LogoIcon,
    LogOutIcon, MagicIcon, MoneyIcon,
    PhotoIcon, PlaneIcon, SettingsIcon
} from '../common/ui/icons';
import { useNavigate } from 'react-router-dom';
import { closeMobileMenu } from './ui/mobile-menu-provider';
import ToggleSidebarButton from './ui/toggle-sidebar-btn';
import ClearCacheBtn from '../projects/admin/ui/planner/clear-cache-btn';
import FlowerMenuItem from './flowers-menu-item';
import { getMe, logout } from '../membership/data/membership-data-service';
import { useEffect, useState } from 'react';
import { showToast } from '../common/domain/toast-provider';
import { getUsernameFromEmail } from '../membership/domain/membership-provider';
import { UserRole } from '../../server-side/membership/membership-interfaces';

interface IMenu {
    showIcons?: boolean;
    hideSideBarToggle?: boolean;
}

const Menu = (props: IMenu) => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<UserRole>(UserRole.Editor);

    const navigate = useNavigate();

    const handleSignOut = async () => {
        await logout();
        navigate('/');
        closeMobileMenu();
    };

    useEffect(() => {
        (async () => {
            const response = await getMe();

            if(!response?.isValid) {
                showToast('Get user data error.');
                return;
            }

            setEmail(response?.email ?? '');
            setRole(response?.role ?? UserRole.Editor);
        })();
    }, []);

    return (
        <nav className={ `flex flex-col bg-slate-900 text-slate-100 px-3 py-3 leading-7 h-screen overflow-auto ${ props.showIcons ? 'w-[130px]' : 'w-[120px]' }` }>

            <button
                onClick={ closeMobileMenu }
                className="xl:hidden mb-4"
                type="button">
                <CloseIcon color={ '#fff' } />
            </button>

            {
                email &&
                <div className="text-xs mb-4">
                    { getUsernameFromEmail(email) }
                </div>
            }

            <div className="mb-4 flex items-center">
                <LogoIcon
                    size={ 30 }
                    fill={ location.hostname === 'localhost' ? 'red' : '#fff' }
                />

                { !props.hideSideBarToggle && <ToggleSidebarButton /> }
            </div>

            <NavLink
                onClick={ closeMobileMenu }
                to="/planner/today"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Planner
                {
                    props.showIcons &&
                    <CheckIcon
                        classes="ml-auto"
                        color={ '#38bca8' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to={ `/planner/calendar` }
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Calendar
                {
                    props.showIcons &&
                    <CalendarIcon2
                        classes="ml-auto"
                        color={ '#fffc00' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/wiki"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                WIKI
                {
                    props.showIcons &&
                    <EditIcon
                        classes="ml-auto"
                        color={ '#c900ff' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/gallery"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Gallery
                {
                    props.showIcons &&
                    <PhotoIcon
                        classes="ml-auto"
                        color={ '#de7b01' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/flashcards"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Cards
                {
                    props.showIcons &&
                    <CardsIcon
                        classes="ml-auto"
                        color={ '#28baff' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/library"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Library
                {
                    props.showIcons &&
                    <BookIcon
                        classes="ml-auto"
                        color={ '#9354ff' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/blocks-year"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                YBlocks
                {
                    props.showIcons &&
                    <BracketsIcon
                        classes="ml-auto"
                        color={ '#6deb17' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/blocks"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Blocks
                {
                    props.showIcons &&
                    <BracketsIcon
                        classes="ml-auto"
                        color={ '#6deb17' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/overview"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Overview
                {
                    props.showIcons &&
                    <PlaneIcon
                        classes="ml-auto"
                        color={ '#fffc00' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            {
                role === UserRole.Admin &&
                <NavLink
                    onClick={ closeMobileMenu }
                    to="/finance"
                    className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                    Finance
                    {
                        props.showIcons &&
                        <MoneyIcon
                            classes="ml-auto"
                            color={ '#e64716' }
                            size={ 20 }
                            strokeWidth={ 1.5 }
                        />
                    }
                </NavLink>
            }

            <NavLink
                onClick={ closeMobileMenu }
                to="/food"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Food
                {
                    props.showIcons &&
                    <AppleIcon
                        classes="ml-auto"
                        color={ '#ffb800' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <FlowerMenuItem
                showIcons={ props.showIcons }
            />

            <NavLink
                onClick={ closeMobileMenu }
                to="/draw-io"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Diagram
                {
                    props.showIcons &&
                    <DiagramIcon
                        classes="ml-auto"
                        color={ '#49adff' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/openu-solver"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                OPENU
                {
                    props.showIcons &&
                    <MagicIcon
                        classes="ml-auto"
                        color={ '#49adff' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </NavLink>

            <NavLink
                onClick={ closeMobileMenu }
                to="/interior-organizer"
                className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                Interior
                {
                    props.showIcons &&
                    <HouseIcon
                        classes="ml-auto"
                        color={ '#d88419' }
                        size={ 20 }
                        strokeWidth={ 2 }
                    />
                }
            </NavLink>

            {
                role === UserRole.Admin &&
                <NavLink
                    onClick={ closeMobileMenu }
                    to="/admin"
                    className="border-b border-slate-700 pb-2 mb-2 flex items-center">
                    Admin
                    {
                        props.showIcons &&
                        <SettingsIcon
                            classes="ml-auto"
                            color={ '#bbbbbb' }
                            size={ 20 }
                            strokeWidth={ 1.5 }
                        />
                    }
                </NavLink>
            }

            <button
                type="button"
                className="flex items-center"
                onClick={ handleSignOut }>
                Logout
                {
                    props.showIcons &&
                    <LogOutIcon
                        classes="ml-auto"
                        color={ '#ffffff' }
                        size={ 20 }
                        strokeWidth={ 1.5 }
                    />
                }
            </button>

            {
                role === UserRole.Admin &&
                <ClearCacheBtn
                    style="menu"
                />
            }
        </nav>
    )
};

export default Menu;